import { Component, DestroyRef, Inject, inject, OnInit, ViewChild } from '@angular/core';
import { BillingInvoice, BillingInvoiceStatus, BillingResolve, Money } from '@shared/models';
import { ActivatedRoute } from '@angular/router';
import { sortArray, TypedRoute } from '@shared/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { breakPoints } from '@core/utils';
import { ArivoPaginatorComponent } from '@shared/components/ui-elements';
import { AmplitudeEventService } from '@core/services';
import { InvoiceDownloaded } from '@integrations/amplitude';

interface InvoicePeriod {
  status: BillingInvoiceStatus | null;
  costs: Money;
  invoices: BillingInvoice[];
  expanded: boolean;
}

@Component({
  selector: 'app-billing-invoices',
  templateUrl: './billing-invoices.component.html',
  styleUrl: './billing-invoices.component.scss',
})
export class BillingInvoicesComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  startOpen: boolean = false;

  invoicePeriods: string[] = [];
  invoices: { [key: string]: InvoicePeriod } = {};

  orderedBy: 'date' = 'date';
  orderedByOrder: 'asc' | 'desc' = 'desc';
  clearedStatus = ['cleared', 'refunded', 'deducted'];
  @ViewChild('paginator') paginator: ArivoPaginatorComponent | undefined;

  constructor(
    @Inject(ActivatedRoute) private route: TypedRoute<BillingResolve>,
    private amplitudeEventService: AmplitudeEventService,
  ) {}

  ngOnInit() {
    this.startOpen = window.innerWidth > breakPoints.md;
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      const invoices = sortArray(data.billingApiResponse.invoices, (x) => x.invoice_date, 'desc');

      let startDate: Date | undefined;
      let endDate: Date | undefined;

      // Group invoices by period
      invoices.forEach((invoice) => {
        const invoiceDateFirstOfMonth = new Date(invoice.invoice_date.getFullYear(), invoice.invoice_date.getMonth(), 1);
        const key = this.getFormattedPeriod(invoiceDateFirstOfMonth);

        if (!startDate || invoiceDateFirstOfMonth < startDate) {
          startDate = invoiceDateFirstOfMonth;
        }
        if (!endDate || invoiceDateFirstOfMonth > endDate) {
          endDate = invoiceDateFirstOfMonth;
        }

        if (key in this.invoices) {
          if (this.invoices[key].status !== 'open' && !this.clearedStatus.includes(invoice.status)) {
            this.invoices[key].status = 'open';
          }
          if (invoice.status !== 'nonrefundable') {
            this.invoices[key].costs.amount += invoice.costs.amount;
          }
          this.invoices[key].invoices.push(invoice);
        } else {
          this.invoices[key] = {
            status: this.clearedStatus.includes(invoice.status) ? 'cleared' : 'open',
            costs: {
              amount: invoice.status !== 'nonrefundable' ? invoice.costs.amount : 0,
              currency: invoice.costs.currency,
            },
            invoices: [invoice],
            expanded: false,
          };
        }
      });

      // Create a list of months between the earliest and the latest invoice period
      if (startDate && endDate) {
        for (let date = new Date(endDate.getTime()); date >= startDate; date.setMonth(date.getMonth() - 1)) {
          const key = this.getFormattedPeriod(date);
          if (!(key in this.invoices)) {
            this.invoices[key] = {
              status: null,
              costs: {
                amount: 0,
                currency: invoices[0].costs.currency,
              },
              invoices: [],
              expanded: false,
            };
          }
          this.invoicePeriods.push(key);
        }
      }
    });
  }

  get paginatedPeriods() {
    if (this.paginator) {
      return this.invoicePeriods.slice(this.paginator.startIndex, this.paginator.endIndex);
    } else {
      return this.invoicePeriods.slice(0, 10);
    }
  }

  orderByDate() {
    this.orderedByOrder = this.orderedBy === 'date' && this.orderedByOrder === 'asc' ? 'desc' : 'asc';
    this.orderedBy = 'date';

    sortArray(this.invoicePeriods, (x) => x, this.orderedByOrder);
  }

  collapse(start: number, end: number) {
    for (let i = start; i < end; i++) {
      this.invoices[this.invoicePeriods[i]].expanded = false;
    }
  }

  getFormattedPeriod(date: Date) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const formattedMonth = month >= 10 ? month : `0${month}`;
    return `${year}-${formattedMonth}`;
  }

  sendInvoiceDownloadedEventToAmplitude(invoice: BillingInvoice) {
    const invoiceAgeInDays = invoice.invoice_date
      ? Math.max(0, Math.floor((Date.now() - invoice.invoice_date.getTime()) / (1000 * 60 * 60 * 24)))
      : 0;

    this.amplitudeEventService.trackEvent(
      new InvoiceDownloaded({
        invoiceStatus: invoice.status,
        invoiceAgeInDays: invoiceAgeInDays,
        invoiceHasAdditionalFees: !!invoice.fees,
      }),
    );
  }
}
